import React, { useCallback, useEffect, useState } from 'react';
import { fetchIntegrationStatus, importProcoreDocuments, fetchImportedProcoreFileIds } from '../services/api';
import { Button, Card, ErrorText, errorMessage } from './ui';
import ProcoreFolderBrowser from './ProcoreFolderBrowser';

// Importing Procore's documents, on the page where documents arrive.
//
// It began life on the Connections page next to the Procore project import,
// which was the wrong place twice over: Connections is about wiring up an
// account, not doing work with it, and it made the person choose a project
// they had already chosen by being inside one. Here the project is known, so
// the card appears only when this project came from Procore and the connection
// is live, and the files land where the person is already working.
//
// Importing is not a sync. Each file is downloaded, stored in this tenant's
// own container and indexed, so an answer months from now cites a document we
// hold rather than one that may have moved inside somebody else's system.
//
// One batch at a time, by design. A folder of two hundred drawings is a long
// synchronous call that downloads, extracts and indexes every one, and it
// would reach the Function timeout well before it reached the end. Capping the
// selection keeps the failure mode honest - a batch either finishes and says
// what came in, or it does not - instead of dying silently in the middle.
const IMPORT_BATCH_MAX = 20;

export default function ProcoreImportCard({
  project,
  // The classification chosen on the page this card sits on. Imported
  // documents are filed with it, so they behave like any other document in
  // this project rather than being a category of their own.
  uploadType = '',
  packageName = '',
  supplier = '',
  tiers = [],
  tags = [],
  summary = '',
}) {
  const procoreProjectId = String(project?.procoreProjectId || '');
  const [connected, setConnected] = useState(false);
  const [browsing, setBrowsing] = useState(false);
  // A Map of id to name rather than a Set of ids, so a selection survives
  // moving around the tree and the result list can still name a file the
  // person ticked three folders ago.
  const [picked, setPicked] = useState(() => new Map());
  const [folderId, setFolderId] = useState('');
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  // Procore file ids already held in this project, so a row can say so. Marked
  // rather than hidden or disabled: re-importing is a legitimate thing to do
  // when a document has been revised in Procore, and it overwrites the same
  // blob and search record rather than making a second copy.
  const [importedIds, setImportedIds] = useState(() => new Set());
  const [importedPartial, setImportedPartial] = useState(false);

  useEffect(() => {
    // Nothing to check for a project that isn't from Procore - and no request
    // made for it either.
    if (!procoreProjectId) return undefined;
    let live = true;
    fetchIntegrationStatus()
      .then((status) => {
        const procore = (status?.providers || []).find((p) => p.id === 'procore');
        if (live) setConnected(Boolean(procore?.connected));
      })
      .catch(() => { if (live) setConnected(false); });
    return () => { live = false; };
  }, [procoreProjectId]);

  // Reloaded after an import as well as on mount, so files just brought in are
  // marked without the page being refreshed.
  const loadImported = useCallback(() => {
    if (!procoreProjectId) return;
    fetchImportedProcoreFileIds({ projectId: procoreProjectId, projectName: project?.name || '' })
      .then((data) => {
        setImportedIds(new Set((data?.fileIds || []).map(String)));
        setImportedPartial(Boolean(data?.truncated));
      })
      // Not surfaced as an error: the browser is still perfectly usable
      // without the marks, and a red message about an annotation would read as
      // the import itself being broken.
      .catch(() => setImportedIds(new Set()));
  }, [procoreProjectId, project?.name]);

  useEffect(() => {
    if (!connected) return;
    loadImported();
  }, [connected, loadImported]);

  // Which folder the browser is showing. Recorded against each pick and sent
  // with the import, because without it the API has no idea where a file lives
  // and walks the entire project to find one whose id it was already given -
  // hundreds of calls for a single file, and Procore's rate limit exhausted.
  //
  // useCallback is not optional here: ProcoreFolderBrowser lists the folder in
  // an effect that depends on this prop, so a fresh function each render would
  // re-fetch forever.
  const onFolderChange = useCallback(({ id } = {}) => {
    setFolderId(id ? String(id) : '');
  }, []);

  function toggleFile(file) {
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(file.id)) next.delete(file.id);
      // The folder travels with the pick rather than being read at import
      // time, because a selection deliberately survives moving around the tree
      // - the file ticked three folders ago is not in the folder on screen now.
      else if (next.size < IMPORT_BATCH_MAX) next.set(file.id, { name: file.name, folderId });
      return next;
    });
  }

  async function runImport() {
    if (!picked.size) return;
    setImporting(true);
    setError('');
    setResults(null);
    try {
      const data = await importProcoreDocuments({
        projectId: procoreProjectId,
        fileIds: [...picked.keys()],
        folderIds: [...new Set([...picked.values()].map((v) => v?.folderId).filter(Boolean))],
        projectName: project?.name || '',
        uploadType,
        packageName,
        supplier,
        tiers,
        tags,
      });
      setResults(Array.isArray(data?.results) ? data.results : []);
      setPicked(new Map());
      // So the rows just imported are marked without a page refresh.
      loadImported();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setImporting(false);
    }
  }

  if (!procoreProjectId || !connected) return null;

  const atLimit = picked.size >= IMPORT_BATCH_MAX;

  return (
    <Card
      title="Import from Procore"
      hint="Files are copied into this project and become searchable in Chat, cited to the page they were read from."
    >
      <ErrorText>{error}</ErrorText>

      {!browsing ? (
        <Button secondary compact onClick={() => { setBrowsing(true); setResults(null); }}>
          Browse Procore documents
        </Button>
      ) : null}

      {/* Said once, where the marks are, rather than left for someone to
          notice: a project past the scan cap may show a file as not imported
          when it is. Re-importing one is a repeated download, not a duplicate
          document, so this is a cost worth naming and not a hazard. */}
      {browsing && importedPartial ? (
        <p className="card-hint">
          This project has more documents than the “already imported” check reads in one go, so some
          files may not be marked.
        </p>
      ) : null}

      {browsing ? (
        <ProcoreFolderBrowser
          projectId={procoreProjectId}
          onFolderChange={onFolderChange}
          rootName={project?.name || 'Documents'}
          renderFile={(file) => {
            const checked = picked.has(file.id);
            const already = importedIds.has(String(file.id));
            // Deliberately NOT given the is-added class the project list uses:
            // that styles a row as muted and non-interactive, which is right
            // for a project that cannot be added twice and wrong here, where
            // the row is still selectable to pull in a revision. The badge
            // alone carries it.
            return (
              <label key={file.id} className="procore-option">
                <input
                  type="checkbox"
                  checked={checked}
                  // Disabled at the limit rather than silently ignoring the
                  // click: a tick that does nothing reads as a bug.
                  //
                  // NOT disabled for an already-imported file: a document gets
                  // revised in Procore, and re-importing is how the current
                  // version gets here. It overwrites the same blob and search
                  // record, so it refreshes rather than duplicates.
                  disabled={!checked && atLimit}
                  onChange={() => toggleFile(file)}
                />
                <span className="procore-option-name">{file.name}</span>
                {file.size ? (
                  <span className="procore-option-code">{Math.max(1, Math.round(file.size / 1024))} KB</span>
                ) : null}
                {already ? <span className="rate-badge">imported</span> : null}
              </label>
            );
          }}
        />
      ) : null}

      {picked.size ? (
        <div style={{ marginTop: 10 }}>
          <p className="card-hint">
            {picked.size} file{picked.size === 1 ? '' : 's'} selected
            {atLimit ? ` — ${IMPORT_BATCH_MAX} at a time, import these and carry on` : ''}.
          </p>
          {/* Where they will be filed, in the words of the choices above. Said
              here because this card is below those cards and a person who
              scrolled past them would otherwise have no idea the import was
              about to inherit anything. */}
          <p className="card-hint">
            Filing as <strong>{uploadType || 'Procore'}</strong>
            {summary ? <> in <strong>{summary}</strong></> : null}
            {tags.length ? <> · {tags.join(', ')}</> : null}
            . Change the cards above to file them differently.
          </p>
          <Button compact onClick={runImport} disabled={importing}>
            {importing ? 'Importing…' : `Import ${picked.size} file${picked.size === 1 ? '' : 's'}`}
          </Button>
        </div>
      ) : null}

      {/* Per-file, because that is where the interesting outcomes are. A
          scanned drawing stores fine and has nothing to index, and the person
          needs to see that here rather than discover it when Chat cannot find
          the document weeks later. */}
      {results ? (
        <div className="procore-picker" style={{ marginTop: 12 }}>
          <p className="card-hint">
            Imported {results.filter((r) => r.ok).length} of {results.length}.
          </p>
          {results.map((r) => (
            <div key={r.id} className="procore-option">
              <span className="procore-option-name">{r.ok ? '✓' : '—'} {r.name}</span>
              <span className="procore-option-code">
                {r.ok
                  ? `${Number(r.chars || 0).toLocaleString()} characters indexed`
                  : r.reason === 'no_text_to_index'
                    ? 'stored, but no text to search — a scan needs analysing first'
                    : r.reason || 'not imported'}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );
}
